import React from 'react';

import { Control, UseFormGetValues, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { FieldErrors } from 'react-hook-form/dist/types/errors';

import { useAssigneeOptionsList } from 'hooks/useAssigneeOptionsList';

import { cardsDataQueryKey } from 'api/queries/cardsData.query';
import { QueryResponse as boardInitResponse } from 'api/queries/getBoardInitLite.query';
import { QueryResponse as CardResponse } from 'api/queries/getMainCard.query';

import { AssigneeAvatarWithSelection } from 'components/common/AssigneeAvatarWithSelection/AssigneeAvatarWithSelection';
import Icon from 'components/common/Icons/base/Icon/Icon';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import { TrackedUntrackedAction } from 'components/common/TrackedUntrackedAction/trackedUntrackedAction';

import {
    IActiveTab,
    TabsSection,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/TabsSection/TabsSection';
import { IAssigneeWithExtraData } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/CreateCardModal/CreateCardModal.types';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/ProgramActivityCardModal/ProgramActivityCardModal.module.scss';
import { IFields } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/ProgramActivityCardModal/ProgramActivityCardModal.types';
import { tabsArrDataMobile } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/ProgramActivityCardModal/ProgramActivityCardModal.utils';
import { CardContent } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/ProgramActivityCardModal/components/CardContent/CardContent';
import { CardHeaderMobile } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/ProgramActivityCardModal/components/CardHeader/CardHeader.mobile';
import { ProgramActivityCardStatusSelect } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/ProgramActivityCardModal/components/ProgramActivityCardStatusSelect/ProgramActivityCardStatusSelect';
import { Button } from 'components/DesignSystem/Button';

interface Props {
    data: CardResponse;
    activeTab: IActiveTab;
    setValue: UseFormSetValue<IFields>;
    control: Control<IFields, any, IFields>;
    setActiveTab: React.Dispatch<React.SetStateAction<IActiveTab>>;
    watch: UseFormWatch<IFields>;
    boardInit: boardInitResponse | undefined;
    errors: FieldErrors<IFields>;
    handleCardClose: () => void;
    register: UseFormRegister<IFields>;
    queryKeys: string[];
    isCardDirty: boolean;
    assignee: IAssigneeWithExtraData | null;
    getValues: UseFormGetValues<IFields>;
}

export const ProgramActivityCardModalMobile = ({
    data,
    activeTab,
    setValue,
    control,
    setActiveTab,
    watch,
    boardInit,
    errors,
    handleCardClose,
    register,
    queryKeys,
    isCardDirty,
    assignee,
    getValues,
}: Props) => {
    const { options: assigneeOptionsList } = useAssigneeOptionsList();

    const handleAssigneeSelection = (value: string) => {
        const { activityId, versionId } = getValues();
        const selectedAssignee = assigneeOptionsList.find((user) => user.assignee.userName === value);
        if (versionId && activityId && selectedAssignee && selectedAssignee.assignee) {
            setValue('assignee', selectedAssignee, { shouldDirty: true });
        }
    };

    return (
        <>
            <CardHeaderMobile control={control} />
            <div className={classes.tabsWithStatusWrapper}>
                <div className={classes.starWrapper}>
                    <ProgramActivityCardStatusSelect
                        control={control}
                        category={watch('category')}
                        originalStatus={watch('originalStatus')}
                        isInterimChanged={data.isInterimChanged}
                    />
                    <TrackedUntrackedAction
                        isTracked={data.isTracked}
                        taskVersionHashCode={data.taskVersionHashCode}
                        invalidateQueriesKey={['getCard', cardsDataQueryKey]}
                        dataTrackId={'card'}
                    />
                    <div>
                        <AssigneeAvatarWithSelection
                            classname={classes.assigneeAvatar}
                            assignee={assignee?.assignee ?? null}
                            handleSelection={handleAssigneeSelection}
                        />
                    </div>
                </div>
                <TabsSection tabs={tabsArrDataMobile} activeTab={activeTab} setActiveTab={setActiveTab} />
            </div>

            <CardContent
                activeTab={activeTab}
                boardInit={boardInit}
                setValue={setValue}
                watch={watch}
                control={control}
                errors={errors}
                data={data}
                queryKeys={queryKeys}
                register={register}
            />
        </>
    );
};
